import { useCallback, type ChangeEvent } from 'react';
import { Button, InputLabel, FormControl, TextField } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useFormContext, Controller, type ControllerRenderProps, type FieldValues } from 'FrontRoyalReactHookForm';
import { Tooltip } from 'Tooltip';

type FileInputProps = {
    name: string;
    label: string;
    // optional custom onChange handler on top of the default one
    onChange?: (resultObject: { getUrl: () => string | undefined; file: File | undefined }) => void;
    accept?: string;
    buttonStyle?: React.CSSProperties;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    hideAcceptTooltip?: boolean;
};

const changeHandler = ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
    const file = files?.[0];
    return { getUrl: () => file && window.URL.createObjectURL(file), file };
};

export const FileInput = ({
    name,
    label,
    onChange,
    accept,
    buttonStyle,
    inputRef,
    hideAcceptTooltip = false,
}: FileInputProps) => {
    const { control } = useFormContext();

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>, field: ControllerRenderProps<FieldValues, string>) => {
            const resultObject = changeHandler(e);
            if (onChange) {
                onChange(resultObject);
                return;
            }
            field.onChange(resultObject);
        },
        [onChange],
    );

    const button = (field: ControllerRenderProps<FieldValues, string>) => (
        <Button variant="inputButton" style={buttonStyle}>
            <CloudUpload />
            <TextField
                type="file"
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, field)}
                style={{
                    opacity: 0,
                    position: 'absolute',
                    left: '0px',
                    top: '0px',
                    height: '100%',
                    width: '100%',
                }}
                slotProps={{ htmlInput: { accept, ref: inputRef } }}
            />
        </Button>
    );

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <FormControl>
                    <InputLabel shrink style={{ marginLeft: '-14px' }}>
                        {label}
                    </InputLabel>
                    <div>
                        {accept && !hideAcceptTooltip && (
                            <Tooltip content={`Accepted file types: ${accept}`}>{button(field)}</Tooltip>
                        )}
                        {!accept && button(field)}
                    </div>
                </FormControl>
            )}
        />
    );
};

export default FileInput;
